<script setup lang="ts">
import type { OnboardingPopupTaskRow } from '~/constants/onboarding-popup-tasks';
import { ref } from 'vue';
import TaskStatus from './TaskStatus.vue';

interface Props {
	rows: OnboardingPopupTaskRow[];
	workspaceRequiredLabel: string;
}

defineProps<Props>();

const emit = defineEmits<{
	action: [row: OnboardingPopupTaskRow];
}>();

const expandedRowId = ref<OnboardingPopupTaskRow['id'] | null>(null);

function toggleRow(row: OnboardingPopupTaskRow) {
	expandedRowId.value = expandedRowId.value === row.id ? null : row.id;
}
</script>

<template>
	<div class="flex flex-col gap-1 sm:hidden">
		<article
			v-for="row in rows"
			:key="row.id"
			class="rounded-md transition-colors"
			:class="{ 'bg-muted': expandedRowId === row.id }"
		>
			<button
				type="button"
				:data-inline-row-id="row.id"
				:data-done="row.done"
				:data-locked="row.disabled"
				class="flex w-full items-center gap-x-3 rounded-md px-3 py-2 text-start focus-visible:outline-none"
				:aria-expanded="expandedRowId === row.id"
				:aria-controls="`onboarding-inline-detail-${row.id}`"
				@click="toggleRow(row)"
			>
				<TaskStatus :done="row.done" />
				<span class="min-w-0 grow text-base font-normal text-toned">
					{{ row.label }}
				</span>
				<div class="grid w-9 shrink-0 place-items-center text-dimmed">
					<DIcon
						:name="row.disabled ? 'lucide:lock-keyhole' : 'lucide:chevron-down'"
						class="
							size-4
							motion-safe:transition-transform motion-safe:duration-(--onboarding-transition-duration) motion-safe:ease-in-out
						"
						:class="{ 'rotate-180': expandedRowId === row.id && !row.disabled }"
					/>
				</div>
			</button>

			<div
				class="
					grid
					motion-safe:transition-[grid-template-rows] motion-safe:duration-(--onboarding-transition-duration)
					motion-safe:ease-in-out
				"
				:class="expandedRowId === row.id ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'"
			>
				<div
					class="min-h-0 overflow-hidden"
					:inert="expandedRowId !== row.id"
					:aria-hidden="expandedRowId !== row.id"
				>
					<div
						:id="`onboarding-inline-detail-${row.id}`"
						:data-test="expandedRowId === row.id ? 'onboarding-inline-detail' : undefined"
						:data-inline-detail-id="expandedRowId === row.id ? row.id : undefined"
						class="ps-10 pe-3 pb-4"
					>
						<p class="text-base/6 text-toned">
							{{ row.description }}
						</p>

						<div class="mt-3 border-t border-default pt-3">
							<div
								v-if="row.disabled"
								:data-inline-locked-id="row.id"
								class="flex items-center gap-2 text-base/6 text-toned"
							>
								<DIcon name="lucide:lock-keyhole" class="size-5 shrink-0" />
								<span>{{ workspaceRequiredLabel }}</span>
							</div>
							<DButton
								v-else
								:data-inline-action-id="row.id"
								trailing-icon="lucide:arrow-right"
								color="neutral"
								variant="solid"
								size="sm"
								class="w-full justify-between text-base"
								@click="emit('action', row)"
							>
								{{ row.actionLabel }}
							</DButton>
						</div>
					</div>
				</div>
			</div>
		</article>
	</div>
</template>
